import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { OperacaoPendenciaItem } from '@/components/Dashboard/operacaoPendencias.util'

type Props = {
  items: OperacaoPendenciaItem[]
  totalCount: number
  loading?: boolean
}

function toneClasses(tone: OperacaoPendenciaItem['tone']): string {
  if (tone === 'danger') {
    return 'border-red-200/80 bg-white [html[data-theme=dark]_&]:border-red-500/25 [html[data-theme=dark]_&]:bg-[var(--bg-card)]'
  }
  return 'border-amber-200/80 bg-white [html[data-theme=dark]_&]:border-amber-500/25 [html[data-theme=dark]_&]:bg-[var(--bg-card)]'
}

export function OperacaoPendenciasSection({ items, totalCount, loading }: Props) {
  if (loading) {
    return (
      <div className='flex items-center gap-2 rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] px-4 py-3.5 text-sm text-text-muted shadow-sm'>
        <Loader2 size={16} className='animate-spin' aria-hidden />
        Carregando pendências…
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className='flex items-center gap-3 rounded-2xl border border-emerald-200/80 bg-emerald-50/50 px-4 py-3.5 shadow-sm [html[data-theme=dark]_&]:border-emerald-500/20 [html[data-theme=dark]_&]:bg-emerald-950/10'>
        <CheckCircle2 size={18} className='shrink-0 text-emerald-600 [html[data-theme=dark]_&]:text-emerald-400' aria-hidden />
        <div>
          <p className='text-sm font-semibold text-text-primary'>Nada pendente</p>
          <p className='text-xs text-text-muted'>Seu pipeline está em dia neste momento.</p>
        </div>
      </div>
    )
  }

  return (
    <section className='space-y-3' aria-label='Pendências comerciais'>
      <div className='flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200/70 bg-red-50/60 px-4 py-3 shadow-sm [html[data-theme=dark]_&]:border-red-500/25 [html[data-theme=dark]_&]:bg-red-950/15'>
        <div className='flex items-center gap-2.5'>
          <AlertTriangle size={18} className='text-red-600 [html[data-theme=dark]_&]:text-red-400' aria-hidden />
          <p className='text-sm font-semibold text-text-primary'>
            {totalCount} {totalCount === 1 ? 'item exige' : 'itens exigem'} sua ação agora
          </p>
        </div>
        {totalCount > items.length ? (
          <Link
            to='/comercial/chamados'
            className='inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:underline [html[data-theme=dark]_&]:text-red-300'
          >
            Ver todas
            <ArrowRight size={12} aria-hidden />
          </Link>
        ) : null}
      </div>

      <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
        {items.map((item) => (
          <Link
            key={item.id}
            to={item.href}
            className={cn(
              'group flex items-start gap-3 rounded-2xl border p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md',
              toneClasses(item.tone),
            )}
          >
            <span
              className={cn(
                'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                item.tone === 'danger'
                  ? 'bg-red-100 text-red-700 [html[data-theme=dark]_&]:bg-red-950/40 [html[data-theme=dark]_&]:text-red-300'
                  : 'bg-amber-100 text-amber-800 [html[data-theme=dark]_&]:bg-amber-950/40 [html[data-theme=dark]_&]:text-amber-300',
              )}
            >
              !
            </span>
            <div className='min-w-0 flex-1'>
              <p className='text-sm font-semibold text-text-primary'>{item.title}</p>
              <p className='mt-0.5 truncate text-xs text-text-muted'>{item.subtitle}</p>
            </div>
            <ArrowRight
              size={16}
              className='mt-1 shrink-0 text-text-muted opacity-0 transition-opacity group-hover:opacity-100'
              aria-hidden
            />
          </Link>
        ))}
      </div>
    </section>
  )
}
